<template>
  <component :is="fieldLabel ? 'b-form-group' : 'div'" class="dropdown-field" :label="fieldLabel">
    <b-dropdown
      class="filter-field"
      :class="{ 'w-100': !!fieldLabel }"
      variant="outline-default"
      menu-class="menu-shadow text-medium dropdown-menu p-0"
      :toggle-class="toggleClass"
      @shown="$emit('shown')"
      @hidden="searchText = ''"
      :boundary="boundary"
      :dropup="dropup"
      :disabled="disabled"
      :lazy="true"
    >
      <template slot="button-content">
        <div class="d-flex align-items-center">
          {{ selectedOption ? (variant === 'pill' ? `${label}:` : '') : label }}
          <component
            :is="clearable ? 'with-close-icon' : 'div'"
            @close="$emit('change', null)"
            class="ml-1 with-close"
            v-if="selectedOption"
          >
            <span :class="{ bold: variant === 'pill' }">{{ selectedOption }}</span>
          </component>
        </div>
      </template>
      <li class="search-field pt-2" v-if="searchable && !loading">
        <b-form-input class="text-medium" placeholder="Search" v-model="searchText" autofocus />
      </li>
      <b-dropdown-item v-if="loading" disabled>
        <div class="flex-center">
          <spinner />
        </div>
      </b-dropdown-item>
      <b-dropdown-item v-else-if="ddOptions && ddOptions.length === 0" disabled>
        <template v-if="searchText"
          >No result matches the query</template
        >
        <template v-else
          >Nothing here</template
        >
      </b-dropdown-item>
      <ul class="dropdown-options py-2" v-else-if="options">
        <b-dropdown-item
          :active="selected === option.id"
          v-for="option in ddOptions"
          @click="$emit('change', option.id)"
          :key="option.id"
          v-no-capture
          >{{ option.value }}
        </b-dropdown-item>
      </ul>
    </b-dropdown>
    <div class="field-error-message mt-1" v-if="error">
      {{ error }}
    </div>
  </component>
</template>

<script>
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import * as R from 'ramda'
import { BDropdownItem, BDropdown, BFormInput, BFormGroup } from 'bootstrap-vue'
import WithCloseIcon from '@/components/WithCloseIcon'
import Spinner from '@/components/Spinner'

/**
 * This is a dropdown
 * It provides multiple styles options (via prop - variant)
 */
export default {
  name: 'Dropdown',
  props: {
    selected: {
      type: [String, Number]
    },
    /**
     * format:  [{id: any, value: any}]
     */
    options: Array,
    label: String,
    fieldLabel: String,
    clearable: {
      type: Boolean,
      default: true
    },
    variant: {
      default: 'default' // default | pill
    },
    loading: {
      default: false
    },
    boundary: {
      default: 'scrollParent'
    },
    dropup: {
      default: false
    },
    disabled: {
      default: false
    },
    searchable: {
      default: false
    },
    errored: {
      default: false
    },
    error: {
      default: ''
    },
    fieldSize: {
      default: 'sm' // available options - sm | md
    },
    toggleBtnClass: {
      default: ''
    }
  },
  components: {
    BDropdownItem,
    BDropdown,
    WithCloseIcon,
    Spinner,
    BFormInput,
    BFormGroup
  },
  mixins: [ComponentIdMixin],
  data() {
    return {
      searchText: ''
    }
  },
  computed: {
    selectedOption() {
      if (this.selected) {
        const option = this.options.find(R.propEq('id', this.selected))
        return option ? option.value : ''
      }
      return ''
    },
    toggleClass() {
      const errorClass = this.errored ? 'errored' : ''
      return `dd-field text-medium d-flex justify-content-between align-items-center ${
        this.variant
      } ${this.fieldSize} ${this.toggleBtnClass} ${errorClass}`
    },
    ddOptions() {
      return this.options.filter(
        option => option.value.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/_errors.scss';

/deep/ .dd-field {
  border-radius: 50rem;
  border: 1px solid $borderTertiary;

  &.errored {
    border-color: $error;
  }
  &.sm {
    padding: 2px 12px;
  }

  &.md {
    padding: 6px 12px;
  }
  &:focus {
    box-shadow: none;
  }

  &.pill {
    background-color: $quinary;
    border: none;
  }
}

.with-close /deep/ .close-icon {
  top: 11px;
  right: 1px;
}

/deep/ .dropdown-menu {
  background: $documentColor;
}
.search-field {
  padding: 0 8px 8px 8px;
}
.dropdown-options {
  max-height: 250px;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin: 0;
}
/deep/ .dropdown-toggle:after {
  margin-left: 1em;
}
.dropdown-field /deep/ legend,
.field-error-message {
  padding-left: 12px;
}
</style>
